export default defineAppConfig({
    ui: {
        colors: {
            orange: '#CC3A00',
            teal: '#042729',
            cream: '#F8F5EE',
        },
        form: {
            base: 'gap-[1.25rem] flex flex-col content-center items-center',
        },
        accordion: {
            slots: {
                item: 'border-b border-gray-light',
                label: 'text-[1.375rem] font-normal',
                trigger: 'py-[1.25rem]'
            }
        },
        navigationMenu: {
            slots: {
                list: 'gap-[2.125rem]',
                linkLabel: 'text-[0.875rem] font-normal tracking-[0.021875rem]',
                childLink: 'before:rounded-none',
                childLinkLabel: 'text-sage hover:text-white hover:underline hover:underline-offset-4',
                viewport: 'ring-0 bg-teal-darkish',
            },
            variants: {
                active: {
                    true: {
                        link: 'text-white hover:text-white',
                        childLink: 'before:bg-transparent text-sage',
                    },
                    false: {
                        link: 'text-sage hover:text-white',
                        childLink: ['hover:before:bg-transparent', 'transition-colors before:transition-colors'],
                    },
                },
                orientation: {
                    horizontal: {
                        link: 'px-0',
                    },
                    vertical: {
                        root: 'flex-col',
                        link: 'flex-row px-0 py-[1.375rem]',
                        childLabel: 'px-0 py-0',
                        childLink: 'py-0 gap-0 before:inset-y-px before:inset-x-0',
                    },
                },
            },
            compoundVariants: [
                {
                    disabled: false,
                    active: false,
                    variant: 'link',
                    class: {
                        link: ['hover:text-white hover:underline hover:underline-offset-4', 'transition-colors'],
                        linkLeadingIcon: ['text-sage group-hover:text-white', 'transition-colors'],
                    },
                },
                {
                    disabled: false,
                    active: false,
                    variant: 'link',
                    orientation: 'horizontal',
                    class: {
                        link: 'data-[state=open]:text-white',
                        linkLeadingIcon: 'group-data-[state=open]:text-white',
                    },
                },
                {
                    disabled: false,
                    active: false,
                    orientation: 'vertical',
                    variant: 'link',
                    class: {
                        link: ['hover:text-white'],
                    },
                },
                {
                    disabled: false,
                    active: true,
                    variant: 'link',
                    class: {
                        link: ['hover:text-white hover:underline hover:underline-offset-4', 'transition-colors'],
                        linkLeadingIcon: ['group-hover:text-default', 'transition-colors'],
                    },
                },
                {
                    disabled: false,
                    variant: 'link',
                    orientation: 'vertical',
                    class: {
                        link: 'px-0 border-b-1 border-green-line py-[1.375rem] data-[state=open]:text-white',
                        linkLeadingIcon: 'group-data-[state=open]:text-white',
                    },
                },
                {
                    orientation: 'vertical',
                    collapsed: false,
                    class: {
                        childList: 'ms-0 border-0 py-0',
                        childItem: 'ps-0 -ms-0 border-b-0 py-0 text-sage',
                        childLink: 'text-sage',
                    },
                },
                {
                    variant: 'link',                    
                    orientation: 'vertical',
                    active: true,
                    collapsed: false,
                    class: {
                        link: 'text-white',
                        childItem: 'text-sage',
                        childLink: 'text-sage',
                    },
                },                
            ],
        },
        button: {
            slots: {
                base: 'rounded-lg whitespace-nowrap',
            },
            variants: {
                size: {
                    md: {
                        base: 'py-[0.9rem] px-[1.125rem] text-[0.9375rem] font-[300] tracking-[0.01875rem] leading-[1.5625rem]',
                    },
                },
                color: {
                    neutral: 'border-teal border',
                },
                variant: {
                    outline: 'bg-transparent ring-0 hover:bg-primary',
                    icon: 'bg-transparent ring-0 hover:bg-primary',
                    transparent: 'bg-none ring-0 hover:text-teal p-0',
                    sage: 'bg-none text-sage ring-0 font-medium hover:text-orange-for-teal p-0 text-[0.9375rem]',
                    link: 'bg-none text-teal-light ring-0 p-0 text-[0.8125rem] font-medium uppercase tracking-[0.065rem] leading-1 underline underline-offset-2 hover:cursor-pointer hover:text-orange-dark',
                    round: 'rounded-full bg-teal-light text-white ring-0 hover:bg-orange-dark hover:text-white',
                    help: 'w-[1.375rem] h-[1.375rem] rounded-full bg-transparent text-teal-gray ring-0 hover:text-orange-dark hover:bg-transparent p-0',
                    'left-tab': 'bg-none border-1 border-r-0 p-[0.8125rem] rounded-none rounded-l-lg hover:bg-primary hover:text-white hover:border-primary disabled:text-teal-light disabled:border-teal-light disabled:bg-transparent disabled:opacity-100',
                    'right-tab': 'bg-none border-1 p-[0.8125rem] rounded-none rounded-r-lg hover:bg-primary hover:text-white hover:border-primary disabled:text-teal-light disabled:border-teal-light disabled:bg-transparent disabled:opacity-100',
                    close: 'rounded-full w-[2.25rem] h-[2.25rem] bg-[var(--magenta)] text-white gap-0 justify-center hover:bg-white hover:text-[var(--magenta)]',
                    login: 'text-white p-[1.125rem] text-[0.8125rem] uppercase tracking-[0.065rem] leading-[0.8125rem] font-normal',
                    'outline-close': 'text-teal border-teal border-1 p-[1.125rem] text-[0.8125rem] uppercase tracking-[0.065rem] leading-[0.8125rem] font-normal hover:bg-primary hover:text-white',
                    image: 'p-0'
                },
            },
            compoundVariants: [
                {
                    color: 'neutral',
                    variant: 'outline',
                    class: 'font-[400] text-[0.8125rem] tracking-[0.065rem] leading-[0.8125rem] uppercase text-teal hover:bg-primary ring-0 hover:text-white disabled:bg-transparent aria-disabled:bg-transparent focus:outline-none focus-visible:ring-1 focus-visible:ring-primary',
                },
                {
                    color: 'neutral',
                    variant: 'outline',
                    class: 'font-[400] text-[0.8125rem] tracking-[0.065rem] leading-[0.8125rem] uppercase text-teal hover:bg-primary ring-0 hover:text-white disabled:bg-transparent aria-disabled:bg-transparent focus:outline-none focus-visible:ring-1 focus-visible:ring-primary',
                },                
                {
                    color: 'orange',
                    variant: 'outline-close',
                    class: 'bg-orange border-0 text-white',
                },                
                {
                    color: 'primary',
                    variant: 'outline',
                    class: 'border-primary hover:bg-primary hover:text-white',
                },
                {
                    color: 'light',
                    variant: 'outline',
                    class: 'border-1 border-teal-gray text-teal-gray hover:bg-primary hover:text-white hover:border-primary',
                },
                {
                    color: 'primary',
                    variant: 'icon',
                    class: 'hover:bg-primary hover:text-white',
                },                
                {
                    color: 'transparent',
                    variant: 'help',
                    class: 'hover:text-orange-dark p-0 hover:border-0',
                },                
                {
                    color: 'transparent',
                    variant: 'round',
                    class: 'bg-transparent text-teal border-teal border-1 hover:border-teal hover:bg-teal',
                },
                {
                    color: 'transparent',
                    variant: 'ghost',
                    class: 'bg-transparent text-sage',
                },
                {
                    color: 'transparent',
                    variant: 'close',
                    class: 'bg-transparent text-teal',
                },                
                {
                    color: 'magenta',
                    variant: 'ghost',
                    class: 'bg-transparent text-magenta uppercase font-normal underline underline-offset-4 text-[0.8125rem] tracking-[0.065rem] leading-[1em] hover:text-orangeish hover:decoration-0',
                },
                {
                    color: 'white',
                    variant: 'ghost',
                    class: 'bg-transparent text-white uppercase underline underline-offset-4 text-[0.8125rem] tracking-[0.065rem] leading-[1em] hover:text-orange-dark hover:decoration-none',
                },
                {
                    color: 'transparent',
                    variant: 'left-tab',
                    class: 'p-[0.8125rem]',
                },
                {
                    color: 'transparent',
                    variant: 'right-tab',
                    class: 'p-[0.8125rem]',
                },
                {
                    color: 'transparent',
                    variant: 'outline',
                    class: 'font-[400] text-[0.8125rem] tracking-[0.065rem] leading-[0.8125rem] uppercase text-sage bg-transparent hover:bg-white ring-1 hover:text-teal focus:outline-none focus-visible:ring-1 focus-visible:ring-primary',
                },
                {
                    color: 'primary',
                    variant: 'transparent',
                    class: 'text-teal-light text-[0.9375rem] font-medium hover:text-teal ring-0 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary',
                },
                {
                    color: 'transparent',
                    variant: 'image',
                    class: 'text-teal-light text-[0.9375rem] font-medium hover:text-teal ring-0 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary',
                },                
                {
                    color: 'primary',
                    variant: 'link',
                    class: 'text-teal-light font-medium hover:text-orange-dark hover:no-underline ring-0 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary',
                },
                {
                    color: 'light',
                    variant: 'round',
                    class: 'bg-transparent h-full text-teal-light border-teal-light border-1 hover:bg-teal hover:border-teal hover:text-white items-center justify-center',
                },   
                {
                    color: 'sage',
                    variant: 'round',
                    class: 'bg-transparent h-full text-sage border-sage border-1 hover:bg-white hover:border-white hover:text-teal items-center justify-center',
                },
                {
                    color: 'teal',
                    variant: 'round',
                    class: 'bg-transparent h-full text-teal-light border-teal-light border-1 hover:bg-teal hover:border-teal hover:text-white items-center justify-center',
                },                                                
            ],
        },
        slider: {
            slots: {
                track: 'bg-cream-line h-[1px]',
                range: 'bg-teal h-[1px]',
                thumb: 'bg-white ring-1 hover:bg-primary hover:cursor-pointer',
            },
            compoundVariants: [
                {
                    orientation: 'horizontal',
                    size: 'md',
                    class: {
                        track: 'h-[1px]',
                    },
                },
            ],
        },
        select: {
            slots: {
                base: 'relative rounded-lg border border-primary text-primary focus:outline-none disabled:cursor-not-allowed',
                arrow: 'fill-default',
                content: 'bg-[var(--magenta)] text-white rounded-lg text-sm py-2.75 pl-4.5 pr-2',
                item: 'my-1 text-sm text-white data-highlighted:not-data-disabled:text-white data-highlighted:not-data-disabled:underline-offset-4 data-highlighted:not-data-disabled:underline data-highlighted:not-data-disabled:before:bg-transparent data-highlighted:not-data-disabled:cursor-pointer',
                trailing: 'p-3',
                trailingIcon: 'text-[var(--magenta)] group-hover:text-white',
            },
            variants: {
                size: {
                    md: 'py-[0.9rem] px-[1.125rem] text-[0.9375rem] font-[300] tracking-[0.01875rem] leading-[1.5625rem]',
                },
                variant: {
                    outline: 'bg-default ring-0 hover:bg-primary hover:text-white',
                    tabs: 'border-0 p-0 text-[1.75rem] text-orange-dark font-medium tracking-[-0.048125rem] leading-[2.125rem]',
                },
            },
            compoundVariants: [
                {
                    color: 'primary',
                    variant: 'outline',
                    class: 'focus-visible::bg-orange bg-transparent'
                },
                {
                    color: 'primary',
                    variant: 'tabs',
                    class: {
                        trailing: 'text-orange-dark end-0 mr-[-4px]',
                        trailingIcon: 'text-orange-dark size-[1.25rem] mt-1',
                    }
                },                
            ],
        },
        drawer: {
            slots: {
                default: 'z-50',
                overlay: 'fixed inset-0 bg-cream/75 backdrop-blur-xs',
                footer: 'border-t-1 border-gray-light p-[2.5rem]',
                container: 'p-0 gap-0',
                body: 'px-[2.5rem]',
                header: 'p-[2.5rem]',
            },
            compoundVariants: [
                {
                    direction: 'right',
                    inset: false,
                    class: {
                        content: 'inset-y-0 right-0 rounded-none bg-white',
                    },
                },
            ],
        },
    },
});
